import * as Sentry from "@sentry/react";
import { OperationVariables } from "@apollo/client";
import { LazyQueryExecFunction } from "@apollo/client/react";
import { Query, QueryYardArgs, Yard } from "../../typing/gql.schema";

type YardQueryFn = LazyQueryExecFunction<{ yard: Query["yard"] }, QueryYardArgs>;
type MyYardsQueryFn = LazyQueryExecFunction<{ myYards: Query["myYards"] }, OperationVariables>;
type UpdateAuthFn = (data: { activeYard: Yard }) => Promise<void>;

interface SwitchActiveYardOptions {
  yardId: string;
  isAdmin: boolean;
  yardQuery: YardQueryFn;
  myYardsQuery: MyYardsQueryFn;
  updateAuth: UpdateAuthFn;
}

/**
 * Shared utility to switch the active yard for the current user.
 * Handles both admin and non-admin cases.
 */
export const switchActiveYard = async (options: SwitchActiveYardOptions): Promise<void> => {
  const { yardId, isAdmin, yardQuery, myYardsQuery, updateAuth } = options;

  try {
    if (isAdmin) {
      const result = await yardQuery({ variables: { id: yardId } });
      if (!result?.data?.yard) {
        throw new Error(`Yard not found: ${yardId}`);
      }
      await updateAuth({ activeYard: result.data.yard });
    } else {
      const result = await myYardsQuery();
      if (!result?.data?.myYards) {
        throw new Error("Error fetching yards");
      }
      const yard = result.data.myYards.find((yard) => yard?.id === yardId);
      if (!yard) {
        throw new Error(`Yard not found in user's yards: ${yardId}`);
      }
      await updateAuth({ activeYard: yard });
    }
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};
